<template>
  <v-dialog
    v-model="dialog"
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 760 : 'auto'"
    style="z-index: 1200 !important;"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        {{ $t('routes.webhookIntegrations.deliveryDetails') }}
        <v-btn icon variant="text" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4 scrollable">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate size="56" width="6" color="primary" :aria-label="$t('routes.webhookIntegrations.loadingDeliveryDetails')" />
          <div class="mt-2">{{ $t('routes.webhookIntegrations.loadingDeliveryDetails') }}</div>
        </div>

        <v-alert v-else-if="errorMessage" type="error" variant="tonal">
          {{ errorMessage }}
        </v-alert>

        <template v-else-if="delivery">
          <v-row density="comfortable">
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.deliveryId') }}</div>
              <div class="text-body-2 text-break">{{ delivery.id }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.eventId') }}</div>
              <div class="text-body-2 text-break">{{ delivery.event_id }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.eventType') }}</div>
              <div>{{ formatEventTypeLabel(delivery.event_type) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.status') }}</div>
              <v-chip :color="getDeliveryStatusColor(delivery.status)" size="small" variant="tonal">
                {{ formatDeliveryStatus(delivery.status) }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.deliveryAttempts') }}</div>
              <div>{{ delivery.attempts || 0 }} / {{ delivery.max_attempts || '-' }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.responseCode') }}</div>
              <div>{{ delivery.last_status_code || '-' }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.payloadVersion') }}</div>
              <div>{{ delivery.payload_version || '-' }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <v-row density="comfortable">
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.createdAt') }}</div>
              <div>{{ formatDeliveryDate(delivery.created_at) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.updatedAt') }}</div>
              <div>{{ formatDeliveryDate(delivery.updated_at) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.lastAttemptAt') }}</div>
              <div>{{ formatDeliveryDate(delivery.last_attempt_at) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.nextAttemptAt') }}</div>
              <div>{{ formatDeliveryDate(delivery.next_attempt_at) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.deliveredAt') }}</div>
              <div>{{ formatDeliveryDate(delivery.delivered_at) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.failedAt') }}</div>
              <div>{{ formatDeliveryDate(delivery.failed_at) }}</div>
            </v-col>
          </v-row>

          <v-alert v-if="hasFailure" type="error" variant="tonal" class="mt-4">
            <div class="font-weight-medium">{{ delivery.last_failure_type || $t('routes.webhookIntegrations.failure') }}</div>
            <div>{{ delivery.last_failure_message || $t('routes.webhookIntegrations.noFailureReason') }}</div>
          </v-alert>

          <div class="text-subtitle-2 mt-5 mb-2">{{ $t('routes.webhookIntegrations.attemptLog') }}</div>
          <v-table v-if="hasAttemptLog" density="compact">
            <thead>
              <tr>
                <th>{{ $t('routes.webhookIntegrations.attempt') }}</th>
                <th>{{ $t('routes.webhookIntegrations.status') }}</th>
                <th>{{ $t('routes.webhookIntegrations.responseCode') }}</th>
                <th>{{ $t('routes.webhookIntegrations.failureReason') }}</th>
                <th>{{ $t('routes.webhookIntegrations.startedAt') }}</th>
                <th>{{ $t('routes.webhookIntegrations.finishedAt') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="attempt in delivery.attempts_log" :key="attempt.id">
                <td>{{ attempt.attempt_number }}</td>
                <td>{{ formatDeliveryStatus(attempt.status) }}</td>
                <td>{{ attempt.status_code || '-' }}</td>
                <td>{{ attempt.failure_message || attempt.failure_type || '-' }}</td>
                <td>{{ formatDeliveryDate(attempt.started_at) }}</td>
                <td>{{ formatDeliveryDate(attempt.finished_at) }}</td>
              </tr>
            </tbody>
          </v-table>
          <v-alert v-else type="info" variant="tonal" density="compact">
            {{ $t('routes.webhookIntegrations.noAttemptsFound') }}
          </v-alert>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  formatDateTime,
  getDeliveryStatusColor,
  getEventTranslationKey,
} from '@/routes/console/webhook-integrations/WebhookConstants.js';

export default {
  name: 'WebhookDeliveryDetailsDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    delivery: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    hasAttemptLog() {
      return (this.delivery?.attempts_log || []).length > 0;
    },
    hasFailure() {
      return Boolean(this.delivery?.last_failure_type || this.delivery?.last_failure_message);
    },
  },
  methods: {
    getDeliveryStatusColor,
    formatDeliveryDate(dateString) {
      return formatDateTime(dateString) || this.$t('routes.webhookIntegrations.noDeliveryTimestamp');
    },
    formatDeliveryStatus(status) {
      return this.$t(`routes.webhookIntegrations.deliveryStates.${status}`);
    },
    formatEventTypeLabel(eventType) {
      return this.$t(`routes.webhookIntegrations.events.${getEventTranslationKey(eventType)}`);
    },
  },
}
</script>
